// GalleryUIFunctions.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FollowButtonInline from "../../components/FollowButtonInline";
import { useTranslation } from "react-i18next";
import Hls from "hls.js";
import { useHlsPrefetch } from "../../../shared/utils/useHlsPrefetch";
import api from '../../../shared/api/axios';
import Modal from "../../settings/Modal";

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

// normalize → remove trailing /api
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

const isUuid = (v?: string | null) => !!v && /^[0-9a-f-]{36}$/i.test(v);

const resolveVideoSrc = (media: MediaEntry): string => {
  if (media.hlsUrl?.trim()) return media.hlsUrl;
  if (media.videoId && isUuid(media.videoId)) {
    return `${API_ORIGIN}/hls/playlist/${media.videoId}`;
  }
  if (media.url && /^https?:\/\//i.test(media.url) && /\.m3u8($|\?)/i.test(media.url)) {
    return media.url;
  }
  return "";
};

const resolvePrefetchVideoId = (media: MediaEntry): string | null => {
  if (media.videoId && isUuid(media.videoId)) return media.videoId;
  if (media.hlsUrl) {
    const match = media.hlsUrl.match(/\/hls\/playlist\/([0-9a-f-]{36})/i);
    if (match) return match[1];
  }
  if (media.url) {
    const match = media.url.match(/\/hls\/playlist\/([0-9a-f-]{36})/i);
    if (match) return match[1];
  }

  return null;
};

const isVideoEntry = (media: MediaEntry) => {
  return !!(
    media.isHls ||
    media.videoId ||
    media.hlsUrl ||
    media.dashUrl ||
    (media.url && /\.m3u8($|\?)/i.test(media.url))
  );
};


export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  style = {},
  onOrientation,
  isLandscape
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  //Play/pause
  const PlayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <path d="M7 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
    <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
  </svg>
);

  //Loudspeakers
  const SpeakerOnIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15 9a4 4 0 0 1 0 6" />
    <path d="M18 7a7 7 0 0 1 0 10" />
  </svg>
);

const SpeakerOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

  /* ---------------- HLS attach ---------------- */
  useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let hls: Hls | null = null;

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    video.load();
  } else if (Hls.isSupported()) {
    hls = new Hls({ startLevel: 0, enableWorker: true });
    hls.loadSource(src);
    hls.attachMedia(video);
  }

return () => {
  try {
    if (hls) hls.destroy();
    video.pause();
    video.removeAttribute("src");
    video.load();
    setIsPlaying(false);
    setProgress(0);
  } catch {}
};
}, [src]);

  /* ---------------- autoplay logic ---------------- */
useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  if (!active) {
    v.pause();
    return;
  }

  if (!userPaused) {
    v.play().catch(() => {});
  }
}, [active, userPaused]);

  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  /* ---------------- time ---------------- */
  const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  v.muted = isMuted;
}, [isMuted]);

  /* ---------------- play/pause ---------------- */
  const togglePlay = () => {
  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    v.pause();
    setUserPaused(true);
  } else {
    v.play().catch(() => {});
    setUserPaused(false);
  }
};


  /* ---------------- render ---------------- */
 return (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    }}
    onMouseEnter={() => setShowControls(true)}
    onMouseLeave={() => setShowControls(false)}
  >
    {/* VIDEO */}
    <video
      ref={videoRef}
      poster={poster}
      muted={isMuted}
      playsInline
      preload="auto"
      controls={false}
      onPointerDown={togglePlay}
      onLoadedMetadata={(e) => {
        emitOrientation(e.currentTarget);
      }}
      onLoadedData={(e) => emitOrientation(e.currentTarget)}
      onCanPlay={(e) => emitOrientation(e.currentTarget)}
      onTimeUpdate={onTimeUpdate}
      onPlay={() => {
  setIsPlaying(true);
  setUserPaused(false);
}}
onPause={() => {
  setIsPlaying(false);
}}
//For the replay this onEnded if I dont want replay remove
onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  v.currentTime = 0;
  v.play().catch(() => {});
}}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        ...style,
      }}
    />

    {/* CONTROLS */}
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: showControls ? 1 : 0,
        transition: "opacity 200ms ease",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: isLandscape ? "7px 14px" : "5px 8px",
        }}
      >
        {/* LEFT — PLAY */}
        <div style={{ display: "flex", alignItems: "center" }}>
    <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay();
  }}
  style={controlBtn}
>

  {isPlaying ? <PauseIcon /> : <PlayIcon />}

</button>

        </div>

        {/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    padding: "0 6px",
    maxWidth: isLandscape ? "70%" : "55%",   // 🔥 limits how wide the scrubber can ever become
    minWidth: 120,     // keeps it usable on small videos
  }}
>
  <input
    type="range"
    min={0}
    max={100}
    value={progress}
    onChange={(e) => handleScrub(Number(e.target.value))}
    className="custom-scrubber"
    style={{
      width: "100%",
      height: 3,
      background: `linear-gradient(
      to right,
      #d8cece 0%,
      #d8cece ${progress}%,
      #555 ${progress}%,
      #555 100%
    )`,
    }}
  />
</div>


      {/* RIGHT — TIME + VOLUME */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
{/* TIME */}
<span
  style={{
    fontSize: 11,
    color: "#bbb",
    whiteSpace: "nowrap",
    marginLeft: -2,
    marginRight: 7,
    fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
  }}
>
  {formatTime(videoRef.current?.currentTime || 0)}/
  {formatTime(videoRef.current?.duration || 0)}
</span>


          {/* SPEAKER */}
          <button
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
  style={controlBtnVolume}
>
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>

          {/* VOLUME */}
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={isMuted ? 0 : volume}
            onChange={(e) => {
              const val = Number(e.target.value);
              setIsMuted(val === 0);
              handleVolume(val);
            }}
            className="volume-slider"
            style={{
              width: isLandscape ? 85 : 65,
              height: 2,
              background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,       // 👈 locks height
  lineHeight: 1,    // 👈 prevents font expansion
  padding: 0,       // 👈 removes vertical shift
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  lineHeight: 1,
  padding: 0,
};


/** ——— Types (structural typing means these can mirror your Gallery.tsx types) ——— */
export interface MediaEntry {
  // image OR fallback url
  url?: string;

  // new video pipeline
  videoId?: string | null;
  isHls?: boolean;
  hlsUrl?: string | null;
  dashUrl?: string | null;

  // optional poster from backend
  posterUrl?: string | null;

  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
}

export interface Post {
  _id: string;
  media: MediaEntry[];
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;
  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;

  // 👇 NEW: ad-specific fields (from /media backend)
  isAd?: boolean;          // true for ads
  adLabel?: string;        // e.g. "Sponsored"
  adCtaText?: string;      // CTA button text
  adUrl?: string;          // click URL
  advertiserName?: string; // optional nice name
}


/** ——— Local helpers/consts used by the UI components ——— */
// Media sizing
const MEDIA_BOX = "calc(100vh - 50px)"; // bigger than your old 117px gap
const MEDIA_HEIGHT = MEDIA_BOX;
const MAX_MEDIA_WIDTH = MEDIA_BOX;

// Styles (duplicated locally so Gallery.tsx doesn't need changes)
const postCardStyle: React.CSSProperties = {
  background:
  'linear-gradient(145deg, rgba(6, 11, 23, 0.95), rgba(4, 7, 15, 0.95))',  
  padding: "15px 20px 0px 20px",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  color: "#888",
};

const mediaScrollContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  overflowY: "hidden",
  gap: "2px",
  paddingTop: 6,
  marginLeft: "-3%",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  touchAction: "auto",
  overscrollBehaviorX: "none",
};

// Asset utils (lightweight, module-scoped)
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm)$/i;

export function isVideoUrl(u: string) {
  try {
    const pathname = new URL(u, window.location.origin).pathname.toLowerCase();
    return VIDEO_EXT_RE.test(pathname);
  } catch {
    return VIDEO_EXT_RE.test(u);
  }
}

const seenPrefetch = new Set<string>();
const prefetchAsset = (url?: string) => {
  if (!url || seenPrefetch.has(url)) return;

  const img = new Image();
  img.decoding = "async";
  img.loading = "eager";
  img.src = url;

  try {
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
  } catch {}

  seenPrefetch.add(url);
};


/** ——— Hook: useMediaOrientation ———
 * Determines if a media URL (image/video) is landscape. For videos, reads metadata.
 */
export function useMediaOrientation(
  url: string,
  isVideo: boolean,
  onResult: (isLandscape: boolean | null) => void
) {
  React.useEffect(() => {
    let alive = true;

    if (!url) {
      onResult(null);
      return;
    }

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;

      const handle = () => {
        if (!alive) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        onResult(w && h ? w > h : null);
        cleanup();
      };
      const err = () => {
        if (alive) onResult(null);
        cleanup();
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", handle);
        v.removeEventListener("error", err);
      };

      v.addEventListener("loadedmetadata", handle);
      v.addEventListener("error", err);
      try {
        v.load();
      } catch {}

      return () => {
        alive = false;
        cleanup();
      };
    } else {
      onResult(null);
    }
  }, [url, isVideo, onResult]);
}

/** ——— Component: ImageAutoFit ———
 * Loads the image to detect orientation, then applies portrait/landscape styles.
 */
export function ImageAutoFit({
  src,
  alt,
  portraitStyle,
  landscapeStyle,
  outerPortraitStyle,
  outerLandscapeStyle,
  onOrientationChange,
  onClick,
  clickable,
  isPrepared,
}: {
  src: string;
  alt?: string;
  portraitStyle: React.CSSProperties;
  landscapeStyle: React.CSSProperties;
  outerPortraitStyle?: React.CSSProperties;
  outerLandscapeStyle?: React.CSSProperties;
  onOrientationChange?: (isLandscape: boolean | null) => void;
  onClick?: () => void;
  clickable?: boolean;
  isPrepared: boolean;
}) {
  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => {
      if (!alive) return;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      const val = w && h ? w > h : null;
      setIsLandscape(val);
      onOrientationChange?.(val ?? null);
    };
    img.onerror = () => {
      if (alive) {
        setIsLandscape(null);
        onOrientationChange?.(null);
      }
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src, onOrientationChange]);

  const style = isLandscape ? landscapeStyle : portraitStyle;
  const outerStyle = isLandscape ? outerLandscapeStyle : outerPortraitStyle;

  return (
    <div style={outerStyle}>
   {isPrepared ? (
  <img
    src={src}
    alt={alt || t('gallery.media.previewAlt')}
    decoding="async"
    loading={isPrepared ? "eager" : "lazy"}
    style={{
      ...style,
      cursor: clickable ? "pointer" : undefined,
    }}
    onClick={onClick}
  />
) : (
  <div
    style={{
      ...style,
      background: "#050505",
    }}
  />
)}

    </div>
  );
}

/** ——— Component: MediaItem ———
 * One tile inside a post’s horizontal gallery. Handles image vs video rendering,
 * hover overlay for music, and orientation styles for images.
 */
export function MediaItem({
  postId,
  index,
  media,
  isActive,
  isPrepared,
  globalMuted,
  registerChild,
  openViewer,
  hoveredKey,
  handleHoverEnter,
  handleHoverLeave,
  onMusicClick,
}: {
  postId: string;
  index: number;
  media: MediaEntry;
  globalMuted: boolean;
  isActive: boolean;
  isPrepared: boolean; // active ±1
  registerChild: (el: HTMLDivElement | null, i: number) => void;
  openViewer: (index: number) => void;
  hoveredKey: string | null;
  handleHoverEnter: (key: string) => void;
  handleHoverLeave: () => void;
  onMusicClick?: (entry: MediaEntry) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isVideo = isVideoEntry(media);
  const mediaUrl = media.url || "";
  const videoSrc = isVideo ? resolveVideoSrc(media) : "";
  const [isVideoLandscape, setIsVideoLandscape] = React.useState<boolean | null>(null);
  const musicLabel = [media.musicTitle, media.musicArtist].filter(Boolean).join(" — ");

  const [isLandscape, setIsLandscape] = React.useState<boolean | null>(null);
    
  // ✅ Orientation only for images
 // image orientation
useMediaOrientation(mediaUrl ?? "", false, (val) => {
  if (!isVideo) setIsLandscape(val);
});

const hoverKey = `${postId}-${index}`;

  return (
    <div
      ref={(el) => registerChild(el, index)}
      onMouseEnter={() => handleHoverEnter(hoverKey)}
      onMouseLeave={handleHoverLeave}
      style={{
        position: "relative",
        height: MEDIA_HEIGHT,
        overflow: "visible",
        paddingBottom: 40,
        marginLeft: "3%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        scrollSnapAlign: "center",
        flex: "0 0 auto",
        width: "auto",
        maxWidth: MAX_MEDIA_WIDTH,
        boxSizing: "border-box",
      }}
    >
{/* Media frame */}
  {/* Media frame OUTER — allows music to escape */}
<div
  style={{
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    }}
  >

  {/* Media frame INNER — clips rounded corners */}
  <div
  style={{
    width: "100%",
    height: "100%",
    borderRadius: 12,
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
  }}
>

{/* 🎬 VIDEO */}
{isVideo && (
  isVideoLandscape ? (
    // LANDSCAPE STRUCTURE
    <div
  style={{
    position: "relative",
    display: "inline-block",
    borderRadius: 12,
  }}
>
<HLSPlayer
  src={videoSrc}
  poster={media.posterUrl || undefined}
  muted={globalMuted}
  active={isActive}
  onOrientation={setIsVideoLandscape}
  isLandscape={true} // or false in the portrait branch
  style={{
    maxHeight: MEDIA_BOX,
    maxWidth: "100%",
    height: "auto",
    width: "auto",
    objectFit: "contain",
    borderRadius: 12,
  }}
/>

{/* Music under video */}
 {musicLabel && hoveredKey === hoverKey && (
  <div
    onClick={(e) => {
      e.stopPropagation();

      if (media.musicId) {
        navigate(`/musicdetails/${media.musicId}`);
      } else {
        onMusicClick?.(media);
      }
    }}
    style={{
      position: "absolute",
      bottom: -22,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 11,
      color: "#807f7f",
      whiteSpace: "nowrap",
      cursor: "pointer",
      zIndex: 20,
      pointerEvents: "auto",
    }}
  >
    🎵 {musicLabel}
  </div>
)}
    </div>
  ) : (
    // PORTRAIT STRUCTURE (original behavior)
    <>
      <HLSPlayer
  src={videoSrc}
  poster={media.posterUrl || undefined}
  muted={globalMuted}
  active={isActive}
  onOrientation={setIsVideoLandscape}
  isLandscape={false}
  style={{
    maxHeight: MEDIA_BOX,
    maxWidth: "100%",
    height: "auto",
    width: "auto",
    objectFit: "contain",
  }}
/>

{musicLabel && hoveredKey === hoverKey && (
  <div
    onClick={(e) => {
      e.stopPropagation();

      if (media.musicId) {
        navigate(`/musicdetails/${media.musicId}`);
      } else {
        onMusicClick?.(media);
      }
    }}
    style={{
      position: "absolute",
      bottom: -20,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 11,
      color: "#807f7f",
      whiteSpace: "nowrap",
      cursor: "pointer",
      zIndex: 6,
      pointerEvents: "auto",
    }}
  >
    🎵 {musicLabel}
  </div>
  )}
 </>
  )
)}

{/* 🖼️ IMAGE */}
{!isVideo && mediaUrl && (
  <ImageAutoFit
  src={mediaUrl}
  alt={t('gallery.media.previewAlt')}
  portraitStyle={{
    height: MEDIA_BOX,
    width: "auto",
    maxWidth: "100%",
    objectFit: "contain",
  }}
  landscapeStyle={{
    width: MEDIA_BOX,  
    height: "auto",
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  }}
  outerPortraitStyle={{
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  }}
  outerLandscapeStyle={{
    display: "flex",
    alignItems: "flex-start", 
    justifyContent: "center",
    width: "100%",
    height: "100%",
    paddingInline: 6,
  }}
  onOrientationChange={setIsLandscape}
  onClick={() => openViewer(index)}
  clickable
  isPrepared={isPrepared}
/>

        )}
        </div>
  </div>

        </div>
      );
    }
    

/** ——— Component: PostSection ———
 * Complete vertical page section for a single post (header, horizontal media strip, footer).
 */
const PostSectionComponent = function PostSection({
  post,
  isActive,
  inWindow,
  globalMuted,
  openViewer,
  navigate,
  onLike,
  onUnlike,
  currentUserId,
  openPresentation,
  onMusicClick,
  onAdClick,
  likedIds,
  likeCounts,
}: {
  post: Post;
  isActive: boolean;
  inWindow: boolean;
  globalMuted: boolean;
  openViewer: (media: MediaEntry[], index: number) => void;
  navigate: ReturnType<typeof useNavigate>;
  onLike: (id: string) => void;
  onUnlike: (id: string) => void;
  currentUserId?: string;
  openPresentation: (postId: string) => void;
  onMusicClick?: (entry: MediaEntry) => void;
  onAdClick?: () => void; 
  likedIds?: Set<string>;
  likeCounts?: Map<string, number>;
}) {
  const { t } = useTranslation();

   // 🧹 cleanup prefetched asset registry when leaving feed
  useEffect(() => {
    return () => {
      seenPrefetch.clear();
    };
  }, []);

const [menuOpen, setMenuOpen] = React.useState(false);
const [reportOpen, setReportOpen] = useState(false);
const [selectedReason, setSelectedReason] = useState<string | null>(null);
const [reportStep, setReportStep] = useState<"list" | "details" | "done">("list");
const [details, setDetails] = useState("");

const handleReportPost = () => {
  setMenuOpen(false);
  setReportOpen(true);
  setReportStep("list");
};

const submitReport = async () => {
  if (!selectedReason) return;

  try {
    await api.post("/block-report/report/post", {
      postId: post._id,
      reasonKey: selectedReason,
      details: details || undefined,
    });

    setReportStep("done");

    setTimeout(() => {
      setReportOpen(false);
      setSelectedReason(null);
      setDetails("");
    }, 1200);

  } catch (err) {
    console.error(err);
    alert(t('gallery.report.failed'));
  }
};

//Report Button
const menuRef = React.useRef<HTMLDivElement | null>(null);

React.useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (!menuRef.current) return;
    if (!menuRef.current.contains(e.target as Node)) {
      setMenuOpen(false);
    }
  };

  if (menuOpen) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [menuOpen]);
//End Report Button

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<HTMLDivElement[]>([]);
  const [currentIdx, setCurrentIdx] = React.useState(0);

  const isAdPost = !!post.isAd || (post as any).type === 'ad';
  const ctaText = post.adCtaText || t('gallery.ad.learnMore');
  const adUrl = post.adUrl;

  const isPrepared = isActive || inWindow;

  const nextMedia = post.media[currentIdx + 1];
  const nextVideoId = nextMedia ? resolvePrefetchVideoId(nextMedia) : null;

    // 🔥 derive likes from context (not from post object)
const likeKey = post.likeKey || post._id;

const liked =
  likedIds?.has(likeKey) ??
  post.likedByUser ??
  false;

const likeCount =
  likeCounts?.get(likeKey) ??
  post.likes ??
  post.totalLikes ??
  0;


  React.useEffect(() => {
  const next = post.media[currentIdx + 1];
  const prev = post.media[currentIdx - 1];

  if (next?.url) prefetchAsset(next.url);
  if (prev?.url) prefetchAsset(prev.url);
}, [currentIdx, post.media]);


  const ownerId = post.user?._id;
  const isOwnPost = !!ownerId && currentUserId === ownerId;
  const goToAuthor = () => {
    if (!ownerId) return;
    navigate(isOwnPost ? "/profile" : `/visitor-profile/${ownerId}`);
  };

  const goChatWithAuthor = () => {
  if (!post.user?._id || isOwnPost) return;

  setMenuOpen(false);

  navigate("/chat", {
    state: {
      toUserId: post.user._id,
      toUserName: post.user.profilename || "",
      toUserPicture: post.user.picture || "",
    },
  });
};

  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  const hoverTimeoutRef = React.useRef<number | null>(null);
  const handleHoverEnter = (key: string) => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredKey(key);
  };
  const handleHoverLeave = () => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredKey(null);
      hoverTimeoutRef.current = null;
    }, 1500);
  };
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  const registerChild = (el: HTMLDivElement | null, i: number) => {
    if (el) itemRefs.current[i] = el;
  };

  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best: { idx: number; ratio: number } | null = null;
        for (const e of entries) {
          const idx = itemRefs.current.findIndex((n) => n === e.target);
          if (idx >= 0) {
            const r = e.intersectionRatio;
            if (!best || r > best.ratio) best = { idx, ratio: r };
          }
        }
        if (best && best.ratio > 0) {
          requestAnimationFrame(() => setCurrentIdx(best!.idx));
        }
      },
      { root, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    itemRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
 }, [post._id, post.media.length]);


 const countryCode = post.location?.countryCode?.toUpperCase();
const translatedCountry = countryCode
  ? t(`countries.${countryCode}`, {
      defaultValue: post.location?.country || countryCode,
    })
  : post.location?.country || "";

const locationLabel =
  post.location?.city && translatedCountry
    ? `${post.location.city}, ${translatedCountry}`
    : translatedCountry
    ? translatedCountry
    : "";

  const dateLabel = t("gallery.post.postedAt", {
    date: new Date(post.createdAt).toLocaleString(),
  });

  const handleCtaClick = () => {
    if (!adUrl) return;
    window.open(adUrl, "_blank", "noopener,noreferrer");
  };

  useHlsPrefetch(isActive ? nextVideoId : null);

  useEffect(() => {
  if (isActive) {
    setCurrentIdx(0);
  }
}, [isActive]);


  return (
    <section
      data-post-id={post._id}
      style={{
        ...postCardStyle,
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden", 
        boxSizing: "border-box",
        scrollSnapAlign: "start",
        scrollSnapStop: "always",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        paddingTop: "15px",
        justifyContent: "flex-start",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginBottom: 1,
        }}
      >
        {/* LEFT: avatar + name + follow (same for ads + organic) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            onClick={goToAuthor}
            style={{
              width: 35,
              height: 35,
              borderRadius: "5%",
              backgroundColor: post.user?.picture
                ? "transparent"
                : "rgb(38, 29, 162)",
              backgroundImage: post.user?.picture
                ? `url(${post.user.picture})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: post.user?.picture ? 0 : "20px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {!post.user?.picture && "👻"}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              onClick={goToAuthor}
              style={{
                cursor: "pointer",
                color: "#bbb",
                fontWeight: 600,
                fontSize: 15,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: 220,
              }}
            >
              {post.user?.profilename || t("gallery.post.unknownUser")}
            </span>

{!isAdPost && (
            <span style={{ display: "inline-flex", flexShrink: 0 }}>
              <FollowButtonInline
                authorId={post.user?._id}
                profilename={post.user?.profilename}
              />
            </span>
            )}

            {/* 3-dot menu */}
{!isOwnPost && !isAdPost && (
  <div ref={menuRef} style={{ position: "relative" }}>
  <button
    onClick={(e) => {
      e.stopPropagation();
      setMenuOpen((v) => !v);
    }}
    style={{
      background: "transparent",
      border: "none",
      color: "#bbb",
      fontSize: 18,
      cursor: "pointer",
      padding: "2px 6px"
    }}
  >
    ⋯
  </button>

{menuOpen && (
  <div
    style={{
      position: "absolute",
      left: "100%",
      top: "50%",
      transform: "translateY(-50%)",
      marginLeft: 8,
      background: "#111",
      border: "1px solid #333",
      borderRadius: 6,
      padding: "3px 5px",
      zIndex: 50,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      whiteSpace: "nowrap",
    }}
  >
  
      <button
        onClick={goChatWithAuthor}
        style={{
    backgroundColor: "#111",
    color: "#bbb",
    border: "1px solid #D4AF37",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
  }}
      >
        💬 Chat
      </button>
 

    <button
      onClick={handleReportPost}
      style={{
    backgroundColor: "#111",
    color: "#bbb",
    border: "1px solid #D24444",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
  }}
    >
      🚩 {t("gallery.report.button")}
    </button>
  </div>
)}
</div>
)}
          </div>
        </div>

        {/* RIGHT: differs for ad vs normal post */}
        {!isAdPost ? (
          // 🌍 Normal post: location + date + buttons (unchanged)
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              fontSize: 13,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "#555",
                whiteSpace: "nowrap",
              }}
            >
  {locationLabel && (
  <button
    onClick={() => {
      const city = post.location?.city?.trim();
      const country = post.location?.country?.trim();
      const countryCode = post.location?.countryCode?.trim();

      if (!city || !(countryCode || country)) return;

      navigate(
        `/location-collection?city=${encodeURIComponent(city)}&country=${encodeURIComponent(
          country || ""
        )}&countryCode=${encodeURIComponent(countryCode || "")}`
      );
    }}
    style={{
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      color: "#555",
      cursor: "pointer",
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
    title={locationLabel}
  >
    📍 {locationLabel}
  </button>
)}
              <span>{dateLabel}</span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => openPresentation(post._id)}
                style={{
                  backgroundColor: "#111",
                  color: "#bbb",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "3px 4.5px",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                🎬 {t("gallery.post.presentationButton")}
              </button>

              <button
                onClick={() =>
                 liked ? onUnlike(likeKey) : onLike(likeKey)
                }
                style={{
                  backgroundColor: liked ? "#ffccd5" : "#111",
                  color: liked ? "#a00" : "#bbb",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "3px 5px",
                  fontSize: "12px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                {liked
                  ? `💔 Unlike (${likeCount})`
                  : `❤️ Like (${likeCount})`}
              </button>
            </div>
          </div>
        ) : (
          // 📢 AD post: CTA button (with link) instead of location/date
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              fontSize: 13,
            }}
          >
            {/* Optional small "Sponsored" label */}
            <span
              style={{
                fontSize: 12,
                color: "#a5b4fc",
                whiteSpace: "nowrap",
              }}
            >
              {post.adLabel || t('gallery.ad.sponsored')}
            </span>

            {/* CTA button with link */}
            {adUrl && (
              <button
                type="button"
                onClick={(e) => {
  e.stopPropagation();  // 🧠 prevents media swipe conflicts
  onAdClick?.();
  handleCtaClick();
}}


                style={{
                  padding: "7px 16px",
                  borderRadius: 999,
                  border: "none",
                  cursor: "pointer",
                  background:
                    "linear-gradient(135deg, #3b82f6, #60a5fa)", // blue, as requested
                  color: "#f9fafb",
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {ctaText}
              </button>
            )}
          </div>
        )}
      </div>

      {/* MEDIA AREA */}
      {isPrepared ? (
        <div
  ref={containerRef}
  style={{
    ...mediaScrollContainerStyle,
      pointerEvents: "auto",
  }}
>
  {post.media.map((m, index) => (
    <MediaItem
      key={m.videoId ? `hls-${m.videoId}` : `${m.url}-${index}`}
      postId={post._id}
      index={index}
      media={m}
      isActive={isActive && index === currentIdx}
      isPrepared={isPrepared}
      globalMuted={globalMuted}
      registerChild={registerChild}
      openViewer={(i) => openViewer(post.media, i)}
      hoveredKey={hoveredKey}
      handleHoverEnter={handleHoverEnter}
      handleHoverLeave={handleHoverLeave}
      onMusicClick={onMusicClick}
    />
  ))}
</div>

      ) : (
        <div
          style={{
            height: MEDIA_HEIGHT,
            borderRadius: 12,
            background: "#050505",
          }}
        />
      )}

      <Modal
  open={reportOpen}
  onClose={() => setReportOpen(false)}
  title={t('gallery.report.title')}
  width={420}
>
  {/* STEP 1 */}
  {reportStep === "list" && (
    <>
      {[
  { key: "spam", label: t('gallery.report.reasons.spam') },
  { key: "hate", label: t('gallery.report.reasons.hate') },
  { key: "harassment", label: t('gallery.report.reasons.harassment') },
  { key: "sexual content", label: t('gallery.report.reasons.sexualContent') },
  { key: "scam or fraud", label: t('gallery.report.reasons.scamOrFraud') },
  { key: "violence", label: t('gallery.report.reasons.violence') },
  { key: "self-harm", label: t('gallery.report.reasons.selfHarm') },
  { key: "other", label: t('gallery.report.reasons.other') },
].map((r) => (
        <button
          key={r.key}
          onClick={() => {
            setSelectedReason(r.key);
            setReportStep("details");
          }}
          style={{
            width: "100%",
            padding: "10px",
            marginBottom: 8,
            background: "#111",
            border: "1px solid #333",
            borderRadius: 6,
            color: "#ddd",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {r.label}
        </button>
      ))}
    </>
  )}

  {/* STEP 2 */}
  {reportStep === "details" && (
    <>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder={t('gallery.report.detailsPlaceholder')}
        style={{
          width: "100%",
          minHeight: 80,
          borderRadius: 6,
          padding: 10,
          background: "#000",
          border: "1px solid #333",
          color: "#fff",
          marginBottom: 12,
        }}
      />

      <button
        onClick={submitReport}
        style={{
          width: "100%",
          padding: "10px",
          background: "#ff4d4d",
          border: "none",
          borderRadius: 6,
          color: "#fff",
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        {t('gallery.report.submit')}
      </button>
    </>
  )}

  {/* STEP 3 */}
  {reportStep === "done" && (
    <div style={{ textAlign: "center" }}>
      ✅ {t('gallery.report.submitted')}
    </div>
  )}
</Modal>
    </section>
  );
}

export const PostSection = React.memo(
  PostSectionComponent,
  (prev, next) => {
    return (
      prev.post._id === next.post._id &&
      prev.isActive === next.isActive &&
      prev.inWindow === next.inWindow &&
      prev.globalMuted === next.globalMuted &&
      prev.likedIds === next.likedIds &&
      prev.likeCounts === next.likeCounts
    );
  }
);


export default {
  useMediaOrientation,
  ImageAutoFit,
  MediaItem,
  PostSection,
};
